import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

interface ProviderConfig { id: string; provider: 'smtp2go' | 'sendgrid'; from_address: string; from_name: string; is_active: number; last_test_at: number | null; last_test_ok: number | null }

export function Settings() {
  const qc = useQueryClient();
  const cfg = useQuery<{ config: ProviderConfig | null }>({ queryKey: ['email-cfg'], queryFn: () => api.get('/settings/email-provider') });

  const [form, setForm] = useState({
    provider: 'smtp2go' as 'smtp2go' | 'sendgrid',
    apiKey: '',
    fromAddress: '',
    fromName: '',
  });

  const save = useMutation({
    mutationFn: () => api.put('/settings/email-provider', {
      provider: form.provider,
      credentials: { apiKey: form.apiKey },
      fromAddress: form.fromAddress,
      fromName: form.fromName,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-cfg'] }),
  });

  const [testTo, setTestTo] = useState('');
  const test = useMutation({
    mutationFn: () => api.post<{ ok: boolean; error?: string }>('/settings/email-provider/test', { to: testTo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-cfg'] }),
  });

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      <section className="bg-white border border-slate-200 rounded p-5">
        <h2 className="font-semibold">Email provider</h2>
        <p className="text-sm text-slate-600 mt-1">Used for sign-in links, board invites, and ballot notices. Per-tenant overrides the operator default.</p>

        {cfg.data?.config && (
          <div className="mt-4 p-3 bg-slate-50 rounded text-sm">
            Current: <strong>{cfg.data.config.provider}</strong> from <code>{cfg.data.config.from_address}</code>
            {cfg.data.config.last_test_at && (
              <span className="ml-2 text-xs">
                (tested {new Date(cfg.data.config.last_test_at).toLocaleString()} —{' '}
                {cfg.data.config.last_test_ok ? <span className="text-green-700">OK</span> : <span className="text-red-700">FAILED</span>})
              </span>
            )}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="mt-4 space-y-3">
          <Field label="Provider">
            <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value as typeof form.provider })}
              className="w-full border border-slate-300 rounded px-3 py-2">
              <option value="smtp2go">SMTP2Go</option>
              <option value="sendgrid">SendGrid</option>
            </select>
          </Field>
          <Field label={`${form.provider} API key`}>
            <input type="password" required value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
              className="w-full border border-slate-300 rounded px-3 py-2 font-mono text-sm" />
          </Field>
          <Field label="From address (must be a verified sender on your provider)">
            <input type="email" required value={form.fromAddress} onChange={(e) => setForm({ ...form, fromAddress: e.target.value })}
              className="w-full border border-slate-300 rounded px-3 py-2" />
          </Field>
          <Field label="From name">
            <input required value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })}
              className="w-full border border-slate-300 rounded px-3 py-2" />
          </Field>
          {save.error && <div className="text-red-600 text-sm">{save.error.message}</div>}
          {save.data ? <div className="text-green-700 text-sm">Saved.</div> : null}
          <button type="submit" disabled={save.isPending} className="bg-brand text-white px-4 py-2 rounded font-medium disabled:opacity-50">
            {save.isPending ? 'Saving...' : 'Save & make active'}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t">
          <h3 className="font-medium">Send a test message</h3>
          <div className="mt-2 flex gap-2">
            <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="recipient@example.com"
              className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm" />
            <button onClick={() => test.mutate()} disabled={test.isPending || !testTo}
              className="px-4 py-2 border border-slate-300 rounded text-sm hover:bg-slate-100 disabled:opacity-50">
              {test.isPending ? 'Sending...' : 'Send'}
            </button>
          </div>
          {test.data && (
            <div className={`mt-2 text-sm ${test.data.ok ? 'text-green-700' : 'text-red-700'}`}>
              {test.data.ok ? 'Test sent.' : `Failed: ${test.data.error}`}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="text-sm font-medium block mb-1">{label}</span>{children}</label>;
}
